/**
 * Compliance Center — P4.6B
 *
 * Two tabs:
 *   1. VAT Returns — Create / Recalc / Review / File / Settle / Lock workflow.
 *   2. Retention Releases — Identify eligible retentions → Create → Approve.
 *
 * Both tabs render journal lineage and policy_id stamping inline so
 * the auditor can trace the GL effect of every workflow action.
 */
import React, { useEffect, useState } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  ShieldCheck, FileText, Lock, Unlock, ChevronRight, CheckCircle2,
  Send, Loader2, AlertCircle, Calculator, Banknote, Calendar, RefreshCw,
  Trash2,
} from "lucide-react";

const SAR = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "SAR", maximumFractionDigits: 0,
  }).format(Number(n || 0));

const Tab = ({ id, active, onClick, icon: Icon, children, testid }) => (
  <button
    data-testid={testid}
    onClick={() => onClick(id)}
    className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all ${
      active === id
        ? "border-slate-900 bg-slate-900 text-white shadow-sm"
        : "border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900"
    }`}>
    {Icon && <Icon className="h-4 w-4" />}
    {children}
  </button>
);

const StatusPill = ({ status }) => {
  const map = {
    draft:     "bg-slate-100 text-slate-700 border-slate-200",
    review:    "bg-amber-50 text-amber-700 border-amber-200",
    filed:     "bg-blue-50 text-blue-700 border-blue-200",
    settled:   "bg-emerald-50 text-emerald-700 border-emerald-200",
    locked:    "bg-rose-50 text-rose-700 border-rose-200",
    approved:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  };
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${map[status] || map.draft}`}>
      {status}
    </span>
  );
};

// ─── VAT Returns panel ────────────────────────────────────────────

const VATReturnsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [acting, setActing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    period_start: "2026-04-01",
    period_end:   "2026-06-30",
    adjustments:  0,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get("/vat-returns?limit=50");
        if (alive) setRows(r.data || []);
      } catch (e) {
        if (alive) toast.error("فشل تحميل إقرارات الضريبة");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const act = async (id, op, qs = "") => {
    setActing(`${id}-${op}`);
    try {
      const r = await axiosInstance.post(`/vat-returns/${id}/${op}${qs}`);
      toast.success(r.data.message || op);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `فشل ${op}`);
    } finally {
      setActing(null);
    }
  };

  // Delete — narrow door: only safe for draft/review or orphan filed
  // returns (no settlement_journal_no). Server enforces this; the UI
  // mirrors that policy so the button only renders when it'll succeed.
  const remove = async (row) => {
    const isOrphan = !row.settlement_journal_no
      && (row.status === "filed" || row.status === "settled" || row.status === "locked");
    const force = isOrphan;
    const label = row.return_number || row.id?.slice(0, 12);
    const confirmMsg = isOrphan
      ? `حذف الإقرار اليتيم "${label}"؟ لا يوجد قيد يومية مرتبط — العملية لا رجعة فيها لكنّها مسجّلة في Audit Log.`
      : `حذف مسودّة الإقرار "${label}"؟ العملية لا رجعة فيها.`;
    if (!window.confirm(confirmMsg)) return;
    setActing(`${row.id}-delete`);
    try {
      await axiosInstance.delete(`/vat-returns/${row.id}${force ? "?force=true" : ""}`);
      toast.success(`تمّ حذف ${label}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحذف");
    } finally {
      setActing(null);
    }
  };

  const create = async () => {
    try {
      await axiosInstance.post("/vat-returns", form);
      toast.success("تم إنشاء الإقرار");
      setShowCreate(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إنشاء الإقرار");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-slate-500">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> جاري التحميل…
    </div>
  );

  const transitions = {
    draft:   { op: "review",  label: "Send for Review", icon: ChevronRight },
    review:  { op: "file",    label: "File to ZATCA",   icon: Send,
                 qs: "?zatca_reference=ZAT-MANUAL" },
    filed:   { op: "settle",  label: "Settle (Post Journal)", icon: Calculator },
    settled: { op: "lock",    label: "Lock Period",     icon: Lock },
    locked:  { op: "unlock",  label: "Unlock (Admin)",  icon: Unlock },
  };

  return (
    <div data-testid="vat-returns-panel" className="space-y-4">
      <div className="flex items-center justify-end">
        <button
          data-testid="vat-create-button"
          onClick={() => setShowCreate(true)}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700">
          + إقرار ضريبي جديد
        </button>
      </div>

      {showCreate && (
        <div data-testid="vat-create-modal"
              className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">
            إنشاء إقرار ضريبة قيمة مضافة
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-500">Period Start</label>
              <input data-testid="vat-period-start" type="date"
                value={form.period_start}
                onChange={(e) => setForm({...form, period_start: e.target.value})}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Period End</label>
              <input data-testid="vat-period-end" type="date"
                value={form.period_end}
                onChange={(e) => setForm({...form, period_end: e.target.value})}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Adjustments (Box 15)</label>
              <input data-testid="vat-adjustments" type="number"
                value={form.adjustments}
                onChange={(e) => setForm({...form, adjustments: parseFloat(e.target.value) || 0})}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)}
                     className="rounded-md border border-slate-200 px-3 py-1 text-xs">إلغاء</button>
            <button data-testid="vat-submit-create" onClick={create}
                     className="rounded-md bg-slate-900 px-3 py-1 text-xs font-medium text-white">إنشاء</button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700">
          إقرارات الضريبة ({rows.length})
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Return #</th>
              <th className="px-4 py-2 text-left">Period</th>
              <th className="px-4 py-2 text-right">Box 13 (Due)</th>
              <th className="px-4 py-2 text-right">Box 14 (Deductible)</th>
              <th className="px-4 py-2 text-right">Box 16 (Net)</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Journal</th>
              <th className="px-4 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody data-testid="vat-returns-rows">
            {rows.map((r) => {
              const t = transitions[r.status];
              return (
              <tr key={r.id} data-testid={`vat-row-${r.id}`}
                  className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono text-slate-700">{r.return_number}</td>
                <td className="px-4 py-2 text-slate-500">{r.period_start} → {r.period_end}</td>
                <td className="px-4 py-2 text-right">{SAR(r.boxes?.box_13_vat_due_on_sales)}</td>
                <td className="px-4 py-2 text-right">{SAR(r.boxes?.box_14_vat_deductible)}</td>
                <td className="px-4 py-2 text-right font-semibold">{SAR(r.net_vat_due)}</td>
                <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                <td className="px-4 py-2 font-mono text-xs text-slate-500">
                  {r.settlement_journal_no || "—"}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {(r.status === "draft" || r.status === "review") && (
                      <button
                        data-testid={`vat-recalc-${r.id}`}
                        disabled={acting === `${r.id}-recalc`}
                        onClick={() => act(r.id, "recalc")}
                        title="إعادة احتساب الصناديق من اليومية (مع فلتر الشركة)"
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                        <RefreshCw className={`h-3 w-3 ${acting === `${r.id}-recalc` ? "animate-spin" : ""}`} />
                        Recalc
                      </button>
                    )}
                    {/* Delete: only draft/review or orphan filed (no journal) */}
                    {(
                      r.status === "draft" || r.status === "review" ||
                      ((r.status === "filed" || r.status === "settled" || r.status === "locked")
                        && !r.settlement_journal_no)
                    ) && (
                      <button
                        data-testid={`vat-delete-${r.id}`}
                        disabled={acting === `${r.id}-delete`}
                        onClick={() => remove(r)}
                        title={!r.settlement_journal_no && r.status !== "draft" && r.status !== "review"
                          ? "إقرار يتيم — لا قيد يومية مرتبط · يمكن حذفه بأمان"
                          : "حذف المسودّة"}
                        className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                        <Trash2 className="h-3 w-3" />
                        {!r.settlement_journal_no && r.status !== "draft" && r.status !== "review" ? "Delete Orphan" : "Delete"}
                      </button>
                    )}
                    {t && (
                      <button
                        data-testid={`vat-action-${r.status}-${r.id}`}
                        disabled={acting === `${r.id}-${t.op}`}
                        onClick={() => act(r.id, t.op, t.qs || "")}
                        className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50">
                        <t.icon className="h-3 w-3" />
                        {acting === `${r.id}-${t.op}` ? "..." : t.label}
                      </button>
                    )}
                  </div>
                </td>
              </tr>);
            })}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                لا توجد إقرارات. ابدأ بإنشاء إقرار جديد.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Retention Releases panel ─────────────────────────────────────

const RetentionReleasesPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get("/retention-releases?limit=50");
        if (alive) setRows(r.data || []);
      } catch (e) {
        if (alive) toast.error("فشل تحميل ضمانات الاحتجاز");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const approve = async (id) => {
    setActing(id);
    try {
      const r = await axiosInstance.post(`/retention-releases/${id}/approve`);
      toast.success(`تم الإفراج — Journal ${r.data.journal_no}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإفراج عن الضمان");
    } finally {
      setActing(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-slate-500">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> جاري التحميل…
    </div>
  );

  return (
    <div data-testid="retention-releases-panel" className="space-y-4">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <strong>Retention Release Workflow</strong>
        <p className="mt-1 text-blue-700">
          عند انتهاء فترة الضمان (DLP) للمشروع، يتم إنشاء سند إفراج لتحويل
          الضمان المُحتجز إلى ذمة قابلة للتحصيل (للعميل) أو الدفع (للمقاول الباطن).
          القيد المُولّد عبر Policy Engine يعكس قيد الاحتجاز الأصلي تلقائياً.
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700">
          Retention Releases ({rows.length})
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">RR #</th>
              <th className="px-4 py-2 text-left">Party</th>
              <th className="px-4 py-2 text-left">Project</th>
              <th className="px-4 py-2 text-left">Source Claim</th>
              <th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2 text-left">Release Date</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Journal</th>
              <th className="px-4 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody data-testid="retention-releases-rows">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`rr-row-${r.id}`}
                  className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono text-slate-700">{r.release_number}</td>
                <td className="px-4 py-2">
                  <span className="inline-flex items-center gap-1">
                    <span className={`h-2 w-2 rounded-full ${
                      r.party_type === "client" ? "bg-emerald-500" : "bg-amber-500"
                    }`} />
                    {r.party_name || r.party_type}
                  </span>
                </td>
                <td className="px-4 py-2 text-slate-500">{r.project_name || "—"}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-500">
                  {r.source_claim_number || "—"}
                </td>
                <td className="px-4 py-2 text-right font-semibold">{SAR(r.amount)}</td>
                <td className="px-4 py-2 text-slate-500">{r.release_date}</td>
                <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                <td className="px-4 py-2 font-mono text-xs text-slate-500">{r.journal_no || "—"}</td>
                <td className="px-4 py-2 text-right">
                  {r.status === "draft" && (
                    <button
                      data-testid={`rr-approve-${r.id}`}
                      disabled={acting === r.id}
                      onClick={() => approve(r.id)}
                      className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50">
                      <CheckCircle2 className="h-3 w-3" />
                      {acting === r.id ? "..." : "Approve"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                لا توجد عمليات إفراج ضمان. أنشئ سند إفراج من تفاصيل المشروع.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────

export default function ComplianceCenter() {
  const [tab, setTab] = useState("vat");

  return (
    <div data-testid="compliance-center" className="space-y-6 p-6" dir="rtl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          <ShieldCheck className="inline -mt-1 mr-2 h-7 w-7 text-blue-600" />
          Compliance Center
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          الإقرار الضريبي والامتثال — VAT Returns · Retention Release · Period Lock
        </p>
      </div>

      <div className="flex flex-wrap gap-2" dir="ltr">
        <Tab id="vat" active={tab} onClick={setTab} icon={FileText}
              testid="tab-vat">VAT Returns</Tab>
        <Tab id="retention" active={tab} onClick={setTab} icon={Banknote}
              testid="tab-retention">Retention Releases</Tab>
      </div>

      <div dir="ltr">
        {tab === "vat"       && <VATReturnsPanel />}
        {tab === "retention" && <RetentionReleasesPanel />}
      </div>
    </div>
  );
}
